import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Droplets, Calendar, MapPin, Loader2, Camera, ArrowLeft,
  MessageCircle, Navigation, Check, Copy,
} from 'lucide-react';

import { toast } from 'sonner';
import PageLayout from '../components/layout/PageLayout';
import { api } from '../services/api';
import { fullAddress, mapsDirectionsUrl, whatsappUrl } from '../lib/utils';

const NAV_LINKS = [
  { label: 'Sobre Nós', to: '/sobre' },
  { label: 'Pregações', to: '/pregações' },
  { label: 'Células', to: '/celulas' },
  { label: 'Eventos', to: '/evento' },
  { label: 'Galeria', to: '/galeria' },
  { label: 'Contato', to: '/contato' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Navegação',
    links: [
      ['Em que Cremos', '/sobre'],
      ['História', '/sobre'],
      ['Eventos', '/evento'],
      ['Células', '/celulas'],
      ['Galeria', '/galeria'],
    ],
  },
  {
    title: 'Institucional',
    links: [
      ['Política de Privacidade', '/privacidade'],
      ['Relatórios', '/relatorios'],
      ['Doações', '/contato'],
      ['Área Administrativa', '/admin'],
    ],
  },
];

const DEFAULT_TEXT =
  'Venha viver um momento de comunhão, palavra e oração conosco. Será uma alegria receber você e sua família!';

export default function Invite() {

  const { id } = useParams();
  const [cell, setCell] = useState(null);
  const [network, setNetwork] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    Promise.all([api.getCells(), api.getNetworks(), api.getGallery()]).then(([cells, networks, gal]) => {
      const c = cells.find((x) => x.id === id);
      setCell(c || null);
      setNetwork(c ? networks.find((n) => n.id === c.networkId) : null);
      // Foto da reunião da célula (última disponível)
      const cellPhotos = gal.filter((g) => g.category === 'celula' && g.cellId === id);
      setPhoto(cellPhotos.length ? cellPhotos[cellPhotos.length - 1].img : null);
      setLoading(false);
    });
  }, [id]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 size={26} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!cell) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
        <MapPin size={30} className="text-ink-faint" />
        <p className="mt-3 font-heading text-lg font-bold text-ink">Convite não encontrado</p>
        <Link to="/celulas" className="mt-4">
          <span className="rounded-full bg-primary px-5 py-2.5 font-heading font-semibold text-white">Ver todas as células</span>
        </Link>
      </div>
    );
  }

  const inviteUrl = `${window.location.origin}/convite/${cell.id}`;
  const inviteText = cell.inviteText || DEFAULT_TEXT;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  }

  return (
    <PageLayout navLinks={NAV_LINKS} footerColumns={FOOTER_COLUMNS}>
      <div data-testid="invite-page">
      {/* Hero com foto da reunião */}
      <div className="relative h-64 overflow-hidden bg-gradient-to-br from-primary to-primary-dark md:h-80">

        {photo ? (
          <img src={photo} alt={`Reunião da ${cell.name}`} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Camera size={40} className="text-white/60" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/30 to-transparent" />
        <div className="absolute inset-x-0 top-0">
          <div className="app-container flex items-center justify-between px-5 pt-5 md:px-8">
            <Link to="/celulas" className="flex items-center gap-1.5 text-sm font-semibold text-white hover:text-white/90" data-testid="invite-back">
              <ArrowLeft size={16} /> Células
            </Link>
            <div className="flex items-center gap-1.5 text-sm font-semibold text-white">
              <Droplets size={16} /> Águas Vivas
            </div>
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0">
          <div className="app-container px-5 pb-5 md:px-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-accent-light">Convite</span>
            <h1 className="mt-1 font-heading text-3xl font-extrabold leading-tight text-white md:text-4xl">{cell.name}</h1>
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-sm font-semibold text-white backdrop-blur">
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: network?.color || '#f59e0b' }} />
              {network?.name}
            </span>
          </div>
        </div>
      </div>

      <div className="app-container py-5">
        {/* Texto dinâmico */}
        <p className="text-base leading-relaxed text-ink" data-testid="invite-text">{inviteText}</p>

        {/* Data e Horário */}
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-soft" data-testid="invite-when">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary-dark">
              <Calendar size={20} />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Data e Horário</p>
              <p className="font-heading font-bold text-ink">{cell.day} às {cell.time}</p>
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-soft" data-testid="invite-address">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-600">
              <MapPin size={20} />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Endereço</p>
              <p className="font-heading font-semibold leading-snug text-ink">{fullAddress(cell.address)}</p>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-6 space-y-2.5">
          <div className="flex gap-2.5">
            <a
              href={mapsDirectionsUrl(cell.address)}
              target="_blank"
              rel="noreferrer"
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white font-heading font-semibold text-ink transition-transform duration-200 hover:-translate-y-0.5"
              data-testid="invite-directions"
            >
              <Navigation size={17} /> Como Chegar
            </a>
            <a
              href={whatsappUrl(cell.leader.phone, `Olá! Vi o convite da ${cell.name} e gostaria de participar.`)}
              target="_blank"
              rel="noreferrer"
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-[#25D366] font-heading font-semibold text-white shadow-soft transition-transform duration-200 hover:-translate-y-0.5"
              data-testid="invite-whatsapp"
            >
              <MessageCircle size={17} /> Quero ir
            </a>
          </div>
          <button
            onClick={copyLink}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary-light font-heading font-semibold text-primary-dark transition-transform duration-200 hover:-translate-y-0.5"
            data-testid="invite-copy"
          >
            {copied ? <Check size={18} className="text-emerald-600" /> : <Copy size={18} />}
            {copied ? 'Link copiado' : 'Copiar link do convite'}
          </button>
        </div>
      </div>
      </div>
    </PageLayout>
  );
}


